import { CrossFilled, NewCheckIcon } from '@/src/assets/icons'
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Typography
} from '@mui/material'
import React, { useEffect, useState } from 'react'
interface optionProps {
  id: number | string
  name: number | string
  value: number | string | object
  count?: number | string
  fullCount?: number | string
}

interface FieldProps {
  variant?: 'filled' | 'outlined' | 'standard'
  label?: string
  fieldClass?: string
  divClass?: string
  defaultValue?: string[]
  value?: string[] | []
  placeholder?: string
  error?: boolean
  helperText?: string | object
  inputLabel?: any
  validation?: object
  options: optionProps[]
  disabled?: boolean
  chipView?: boolean
  multiChips?: boolean
  popperLoading?: boolean
  disablePrev?: boolean
  showCheckOnly?: boolean
  RenderCustomLabel?: React.ElementType
  customChange?: (data: any) => void
  fetchOption?: (data: any) => void
  popperOption?: { active_count: number; list: [] }
}

const MultiChipSelect = ({
  disablePrev = false,
  chipView = false,
  multiChips = false,
  label = '',
  disabled = false,
  inputLabel = '',
  defaultValue,
  placeholder,
  helperText,
  error,
  value = [],
  validation,
  variant = 'outlined',
  fieldClass = '',
  divClass = '',
  options = [
    {
      id: 0,
      name: '',
      value: '',
      count: 0
    }
  ],
  showCheckOnly = false,
  RenderCustomLabel,
  customChange,
  fetchOption,
  popperOption,
  popperLoading
}: FieldProps) => {
  const [selected, setSelected] = useState(value ?? [])
  const [selectedOption, setSelectedOption] = useState(null)
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setSelectedOption(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  const removeItem = (itemToRemove: string) => {
    handleClose()
    setSelected(prevSelected => prevSelected.filter(item => item !== itemToRemove))
    if (customChange) {
      customChange(selected.filter(item => item !== itemToRemove))
    }
  }

  const handleSelect = (event: any) => {
    if (disabled) return
    setSelected(event.target.value)
    if (customChange) {
      customChange(event.target.value)
    }
  }

  useEffect(() => {
    if (defaultValue) {
      setSelected(defaultValue)
      if (customChange) {
        customChange(defaultValue)
      }
    }
  }, [])

  const handleSelectedOption = (opt: any) => {
    setSelectedOption(opt)
    if (fetchOption) {
      fetchOption(opt)
    }
  }

  const renderCustomLabel = (selected: string[]) => {
    if (multiChips) {
      if (selected?.length === 0) {
        return <Typography style={{ color: ' rgba(0, 0, 0, 0.44)' }}>{placeholder}</Typography>
      }
      return (
        <Box
          className=" flex-wrap rounded=[0.625rem]"
          display={'flex'}
          alignItems={'center'}
          gap={'0.5rem'}
          onClick={(e: any) => {
            handleClick(e)
          }}
        >
          {selected?.map((item, index) => (
            <div
              onClick={(e: any) => {
                handleSelectedOption(item)
              }}
              key={index}
            >
              <div
                className={`flex  items-center gap-x-1.5  bg-[rgba(71,41,255,0.08)] rounded-[0.5rem]   ${
                  item === selectedOption
                    ? ' border-[3px] py-[0.100rem] px-[0.425rem]'
                    : 'py-[0.313rem] px-[0.625rem]'
                } border-primary-main`}
                onMouseDown={event => event.stopPropagation()}
              >
                <Typography
                  style={{ fontWeight: '500px' }}
                  // variant="chipLabelBold"
                >
                  {options?.find((idx: optionProps) => idx.value === item)?.name}
                </Typography>
                {options?.find((idx: optionProps) => idx.value === item)?.fullCount && (
                  <Typography>
                    {`(${options?.find((idx: optionProps) => idx.value === item)?.fullCount})`}
                  </Typography>
                )}
                <div
                  onClick={e => {
                    e.stopPropagation()
                    removeItem(item)
                  }}
                >
                  <CrossFilled />
                </div>
              </div>
            </div>
          ))}
        </Box>
      )
    } else {
      if (selected.length === 0) {
        return <Typography color="text.secondary">Select</Typography>
      } else if (selected.length === 1) {
        return options?.find((idx: optionProps) => idx.value === selected[0])?.name
      } else {
        if (chipView) {
          return (
            <span>
              <span className="bg-secondary-lighterGray rounded-sm p-[4px]">
                {selected[0] === 'Annual Report' ? 'Company Reports' : selected[0]}
              </span>
              <span>+{selected.length - 1}</span>
            </span>
          )
        }
        return `${selected[0]} ..+${selected.length - 1}`
      }
    }
  }

  const getDisabledValue = (val: number | string | object) => {
    if (defaultValue && Array.isArray(defaultValue)) {
      return defaultValue.length && typeof val === 'string' ? defaultValue.includes(val) : false
    }
  }

  return (
    <div className={`${divClass}`}>
      {RenderCustomLabel && <RenderCustomLabel />}
      {inputLabel && (
        <InputLabel className="font-semibold text-secondary-main text-[0.875rem]">
          {inputLabel}
        </InputLabel>
      )}
      <FormControl error={error} className="w-full">
        <Select
          label={label}
          disabled={disabled}
          variant={variant}
          {...validation}
          MenuProps={{
            PaperProps: {
              className: 'max-h-[300px] overflow-y-auto'
            }
          }}
          defaultValue={defaultValue ?? []}
          autoComplete="off"
          onChange={handleSelect}
          className="rounded-[0.625rem] min-h-14 max-h-48 "
          value={selected ?? []}
          multiple
          placeholder={placeholder}
          error={error}
          fullWidth
          inputProps={{
            className: `${fieldClass}`
          }}
          renderValue={renderCustomLabel}
          displayEmpty
        >
          {options?.map((item: optionProps) => (
            <MenuItem
              disabled={disablePrev ? getDisabledValue(item.value) : false}
              key={item?.id}
              value={item?.id}
              className="flex justify-between"
            >
              <div className="flex justify-start items-center">
                {showCheckOnly ? (
                  <NewCheckIcon
                    classes={`fill-primary-main mr-4 ${
                      selected?.find(val => val === item?.value) ? '' : 'invisible'
                    } `}
                  />
                ) : (
                  <Checkbox checked={selected?.find(val => val === item?.value) ? true : false} />
                )}
                <p>{item?.name}</p>
              </div>
              <div className="text-[rgba(0,0,0,0.6)]">{item?.count}</div>
            </MenuItem>
          ))}
        </Select>

        <FormHelperText id="recipients-helper-text" className="text-red-700 h-0">
          {typeof helperText === 'string' ? helperText : ''}
        </FormHelperText>
      </FormControl>

      <Popover
        id={id}
        className="mt-4 "
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        {popperLoading ? (
          <div className="bg-[#272727] h-[20rem] flex justify-center items-center w-72">
            <CircularProgress size={20} />
          </div>
        ) : (
          <div>
            <Box className="max-h-[20rem] h-[20rem] overflow-y-auto overflow-x-hidden bg-[#272727]  w-72 flex-col flex">
              {popperOption?.list?.map((item: any, index: number) => {
                return (
                  <div key={item?.id}>
                    {index === 0 && (
                      <div key={item?.id} className="bg-[#272727]  w-72  cursor-pointer">
                        <Typography className=" text-x font-normal text-white pl-4  w-[99%] truncate pb-3">
                          {popperOption?.active_count} active contacts
                        </Typography>
                      </div>
                    )}
                    <div key={item?.id} className="  w-72 rounded-2xl cursor-pointer mb-3">
                      <Typography
                        variant="inputValue"
                        className="py-4  text-white pl-4 max-w-64 w-full truncate"
                      >
                        {(item?.full_name ?? item?.first_name)
                          ? `${item?.first_name} ${item?.last_name}`
                          : item?.email}
                      </Typography>
                    </div>
                  </div>
                )
              })}
            </Box>
          </div>
        )}
      </Popover>
    </div>
  )
}

export default MultiChipSelect
